import { IonButton, IonCheckbox, IonItem, IonProgressBar, IonToast } from "@ionic/react";
import { tuple } from "functions/FunnyTypes";
import { CollapsableTable, CollapseLevel } from "components/CollapsableTable";
import { OptionsButton }  from "components/OptionsButton";
import { OverviewCard }   from "components/OverviewCard";
import { Page }           from "components/Page";
import { TimeRangeInput } from "components/TimeRangeInput";
import { EnergyChart }    from "components/charts/EnergyChart";
import { energyUnits }    from "components/charts/EnergyLegend";
import { DvcPage }        from "navigation/DvcPage";
import { RouterMap }      from "navigation/RouterMap";
import { SaveAsCSV, SaveAsXLSX } from "functions/ExportEnergies";
import { Glob, useLoc }          from "functions/Language";
import { Resolution, useEnergyLoader }   from "functions/networking/EnergyLoading";
import { IsFromDevice, RType, TYPE_PLA } from "functions/networking/NetTypes";
import { useEnergyData }         from "functions/useEnergyData";
import { FC, useReducer, useState } from "react";

import { optionsOutline, statsChartOutline } from "ionicons/icons";


const route = RouterMap.energy;

const EnergyTable : FC<{
  onlineData: RType<"Online", typeof TYPE_PLA> | null,
	collapsable?: boolean,
}> = (props) => {
	const counters = props.onlineData?.Data.Energy_T1;
	const loc = useLoc(glob);

	return <div style={{width: "100%"}} className="lastchild energytable">
		<CollapsableTable
			title={loc.counters}
			collapse={props.collapsable ? CollapseLevel.closeAll : undefined}
			uncollapsableChildren
			headerFields={[""]}
			data={[
				[
					{ values: [counters?.Consumption.Active_kWh ?? null],       formatter: formatLargeInt, label: loc.quantNames[0], unit: energyUnits[0], mult: 1 },
					{ values: [counters?.Consumption.Inductive_kvarh ?? null],  formatter: formatLargeInt, label: loc.quantNames[1], unit: energyUnits[1], mult: 1 },
					{ values: [counters?.Consumption.Capacity_kvarh ?? null],   formatter: formatLargeInt, label: loc.quantNames[2], unit: energyUnits[2], mult: 1 },
				],

				[
					{ values: [counters?.Distribution.Active_kWh ?? null],      formatter: formatLargeInt, label: loc.quantNames[3], unit: energyUnits[0], mult: 1 },
					{ values: [counters?.Distribution.Inductive_kvarh ?? null], formatter: formatLargeInt, label: loc.quantNames[4], unit: energyUnits[1], mult: 1 },
					{ values: [counters?.Distribution.Capacity_kvarh ?? null],  formatter: formatLargeInt, label: loc.quantNames[5], unit: energyUnits[2], mult: 1 },
				],
			]}
		/>
	</div>;
}

const OverviewTable : FC<{
  onlineData: RType<"Online", typeof TYPE_PLA> | null
}> = (props) => {
	const loc = useLoc(glob);
	return <OverviewCard title={loc.title}>
		<EnergyTable onlineData={props.onlineData} />
	</OverviewCard>
}

const Content : FC = () => {
  const loc = useLoc(glob);

  const startState = useState(StartDate);
  const endState = useState(new Date);
  const [rangeGen, advanceRangeGen] = useReducer((v : number) => v + 1, 0);

  const [err, setErr] = useState<string | null>(null);

  const [desiredStart, setDesiredStart] = useState<Date>(startState[0]);
  const [desiredEnd, setDesiredEnd] = useState<Date>(endState[0]);
  const [desiredRes, setDesiredRes] = useState(Resolution.month);

  const [power, setPower] = useState(false);

  const loader = useEnergyLoader(desiredStart, desiredEnd, desiredRes);

  const data = useEnergyData(loader.data.current, loader.suggestedRes, loc.quantNames, power, loader.minorDataVersion);

  const Options = <OptionsButton icon={optionsOutline} title={loc.options}>
    {Close => <>
      <TimeRangeInput load={(start, end, res) => {
        Close();
        setDesiredStart(start);
        setDesiredEnd(end);
        setDesiredRes(res);
        advanceRangeGen();
      }} />

      <IonItem>
        <IonCheckbox checked={power} onIonChange={ev => setPower(ev.detail.checked)}>{loc.power}</IonCheckbox>
      </IonItem>
      <IonItem>
        <IonButton
          disabled={loader.progress < 1}
          onClick={async function ()
          {
            try {
              await SaveAsXLSX(data, loc.xlsxName);
            } catch (e) {
              console.error(e);
              setErr(loc.exportError);
            }
          }}
          style={{ height: "3em", "--border-radius": "6px" }}
        >{loc.exportXlsx}</IonButton>
        <IonButton
          disabled={loader.progress < 1}
          onClick={async function ()
          {
            try {
              await SaveAsCSV(data, loc.csvName)
            } catch (e) {
              console.error(e);
              setErr(loc.exportError);
            }
          }}
          style={{ height: "3em", "--border-radius": "6px" }}
        >{loc.exportCsv}</IonButton>
      </IonItem>

      <IonToast
        isOpen={err !== null}
        duration={5000}
        onWillDismiss={() => setErr(null)}
        message={err ?? ""}
        position="top"
        buttons={[{
          text: "OK",
          role: "cancel"
        }]}
      />
    </>}
  </OptionsButton>
	;

  return <Page title={loc.title} loaders={loader.loaders} additionalButtons={Options}>
    <IonItem lines="none" style={{ display: loader.progress >= 1 ? "none" : "block" }}>
      <IonProgressBar type="determinate" value={loader.progress} />
    </IonItem>
    <EnergyChart
      hidden={loader.progress < 1}
      data={loader.data.current}
      resolution={loader.suggestedRes}
      updateRes={(r, s, e) => {
        setDesiredRes(r);
        setDesiredStart(s);
        setDesiredEnd(e);
      }}
      start={desiredStart}
      end={desiredEnd}
      majorDataVersion={loader.majorDataVersion}
      rangeGen={rangeGen}
      power={power}
      edata={data}
    />
		{loader.onlnieData && IsFromDevice(loader.onlnieData, TYPE_PLA) &&
			<EnergyTable collapsable onlineData={loader.onlnieData} />
		}
  </Page>
}


function formatLargeInt(v : number) {
  v = Math.abs(v / 10);
  let tail = "." + (v % 1).toFixed(1)[2];
  let mid = (v % 1e3).toFixed(0);
  v = Math.floor(v / 1000);

  while (v) {
    tail = ' ' + mid.padStart(3, '0') + tail;
    mid = (v % 1e3).toFixed(0);
    v = Math.floor(v / 1000);
  }
  return mid + tail;
}

function StartDate() {
  let date = new Date();
  date.setMonth(date.getMonth() - 6, 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

const Overview : FC = () => {
  const loc = useLoc(glob);
  const [start,] = useState<Date>(StartDate); // fix nekonečné smyčky
  const [end,] = useState(new Date)
  const loader = useEnergyLoader(start, end, Resolution.month);
  const data = useEnergyData(loader.data.current, Resolution.month, loc.quantNames, false, loader.minorDataVersion);

  return <OverviewCard href={route} title={loc.title}>
    <IonProgressBar type="determinate" value={loader.progress} style={{ visibility: loader.progress >= 1 ? "hidden" : "visible" }} />
    <EnergyChart
      data={loader.data.current}
      resolution={Resolution.month}
      simple
      edata={data}
    />
  </OverviewCard>
}


const glob = Glob({
	en: {
		title : "Energy",

		power: "Show average power",

		quantNames: [
			"Active consumption", "Reactive L consumption", "Reactive C consumption",
			"Active distribution", "Reactive C distribution", "Reactive L distribution"
		] as tuple<string, 6>,

		counters: "Energy counters",

		exportCsv: "Export to .csv",
		csvName: "energies.csv",
		exportXlsx: "Export to .xlsx",
		xlsxName: "energies.xlsx",
		exportError: "Exporting failed.",

		options: "Options",
	},
	cs: {
		title: "Energie",

		power: "Zobrazovat průměrný výkon",

		quantNames: [
			"Činný odběr", "Jalový L odběr", "Jalový C odběr",
			"Činná dodávka", "Jalová C dodávka", "Jalová L dodávka"
		],

		counters: "Hodnoty elektroměru",

		exportCsv: "Exportovat do .csv",
		csvName: "energie.csv",
		exportXlsx: "Exportovat do .xlsx",
		xlsxName: "energie.xlsx",
		exportError: "Exportování selhalo.",

		options: "Možnosti",
	},
	pg: {
		title: "Energia",

		power: "Mostrar potência média",

		quantNames: [
			"Consumo ativo", "Consumo reativo L", "Consumo reativo C",
			"Fornecimento ativo", "Fornecimento reativo C", "Fornecimento reativo L",
		],

		counters: "Valores do medidor",
		exportCsv: "Exportar para .csv",
		csvName: "energia.csv",
		exportXlsx: "Eport para .xlsx",
		xlsxName: "energia.xlsx",
		exportError: "Erro de exportação",

		options: "Opções"
	},
	es: {
		title: "Energía",

		power: "Mostrar potencia media",

		quantNames: [
			"Consumo activo", "Consumo reactivo L", "Consumo reactivo C",
			"Suministro activo", "Suministro reactivo C", "Suministro reactivo L",
		],

		counters: "Valores de contador",
		exportCsv: "Exportar a .csv",
		csvName: "energia.csv",
		exportXlsx: "Eport a .xlsx",
		xlsxName: "energia.xlsx",
		exportError: "Exportación fallida",

		options: "Opciones"
	},
} as const);


export const Energy : DvcPage & {
  overview : typeof Overview,
  table: typeof OverviewTable,
} = {
  page : Content,
  path : route,
  title : glob,
  icon : statsChartOutline,
  overview : Overview,
  table: OverviewTable,

  available(dvc) {
    return dvc._type === TYPE_PLA && dvc.Equipment.Data_flash;
  }
}

